import { useMemo, useRef } from "preact/hooks"
import { useState, HTMLAttributes } from "preact/compat"
import { download_export, exactCurrencyFormatter, isGuayakiUser, useFetch, useAsyncEffect } from "@/utils"
import { coding_pill_notice, display_coding_amount } from "@/utils/transaction_amount"
import { should_show_detail_spinner } from "@/utils/detail_refresh.ts"
import { CopyableCell } from "@/components/copyable_cell.tsx"
import {
  api_fetch,
  api_fetch_throws,
  CheckDetail as CheckDetailData,
  TransactionJson,
  DeductionListJson,
  updateCheck,
  is_check_number_conflict,
  BackupViewResponse,
  type DeductionKind,
} from "@/api"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge.tsx"
import { ArrowLeft, ChevronDown, ChevronLeft, ChevronRight, Download, PanelRightClose, PanelRightOpen, Upload, Loader2, Info, Wand2 } from "lucide-react"
import { Link } from "wouter-preact"
import { FileViewer } from "./file_viewer.tsx"
import { PdfViewer, BoundingBox } from "./pdf_viewer.tsx"
import { CenterSpinner } from "@/app/loading"
import { DetailNavbar } from "@/app/navbar.tsx"
import { format, parseISO } from "date-fns"
import { RefreshFlag, triggerRefreshFlag } from "@/refresh_flag.tsx"
import { useDropzone } from "react-dropzone"
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  flexRender,
  createColumnHelper,
  SortingState,
  ColumnFiltersState,
  RowSelectionState,
  Table,
  Row,
} from "@tanstack/react-table"
import { AccountingStatus } from "./accounting_transaction.tsx"
import { OrgConfigState, loadOrgFieldConfigs } from "@/org_config/state.tsx"
import { useOrgSettings, loadOrgSettings } from "@/org_settings/state.tsx"
import { TableControls } from "@/deduction_table/table_controls.tsx"
import { ColumnHeader } from "@/deduction_table/column_header.tsx"
import { capitalCase } from "change-case"
import { fuzzyFilter } from "@/utils/filters.ts"
import { selectColumn, categoryColumn, categoryDescriptionColumn, dcColumn, backupColumn, statusColumn, backup_parse_sort_rank, backup_parse_cell_label } from "@/deduction_table/columns.tsx"
import { BulkCodingWizRowBadge } from "./BulkCodingWizProgress.tsx"
import { DeductionTableSelectionBar } from "@/deduction/DeductionTableSelectionBar.tsx"
import { type SelectItem as AccountSelectItem } from "./autocomplete_select.tsx"
import { CodingCell, coding_display_text } from "./coding_cell.tsx"
import { LedgerAccounts, loadAccountCodes } from "./accounts.tsx"
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs.tsx"
import { toast } from "@/components/ui/use-toast.ts"
import { NOT_A_CHECK_PDF_TOAST } from "./check_upload_messages.ts"
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from "@/components/ui/alert-dialog.tsx"
import { CheckDetailState } from "./check_detail_state.tsx"
import { InlineTransactionDetail } from "./InlineTransactionDetail.tsx"
import { CashApplicationControl } from "./CashApplicationControl.tsx"
import { AutoCodePreviewDialog } from "./AutoCodePreviewDialog.tsx"
import { CodingStrip } from "./CodingStrip.tsx"
import { LoadingSpinner } from "@/app/loading.tsx"
import { evaluateAmountInput } from "./amount_expression.ts"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip.tsx"
import { UserState } from "@/auth/user.tsx"
import { createAccountingOrgColumns } from "@/transactions/columns.tsx"
import { AccountingFilePane } from "./AccountingTransactionContent.tsx"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select.tsx"
import { check_api_path, decode_check_number } from "./check_path"

interface EditableAmountFieldProps {
  value: number | undefined
  checkId: string | undefined
  onUpdate: () => void
}

function EditableAmountField({ value: initial, checkId, onUpdate }: EditableAmountFieldProps) {
  const [loading, setLoading] = useState(false)
  const [editing, setEditing] = useState(false)
  const [tooltipOpen, setTooltipOpen] = useState(false)
  const [localValue, setLocalValue] = useState(initial?.toString() ?? "")
  const lastPropValue = useRef(initial)

  if (initial !== lastPropValue.current) {
    lastPropValue.current = initial
    setLocalValue(initial?.toString() ?? "")
  }

  async function handleSave() {
    setEditing(false)
    if (!localValue.trim()) {
      setLocalValue(initial?.toString() ?? "")
      return
    }
    const newAmount = evaluateAmountInput(localValue)
    if (newAmount === null) {
      setLocalValue(initial?.toString() ?? "")
      toast({
        variant: "destructive",
        title: "Invalid amount",
        description: "Please enter a valid number or calculator expression.",
      })
      return
    }
    if (newAmount === initial) {
      setLocalValue(initial?.toString() ?? "")
      return
    }
    setLoading(true)
    try {
      await updateCheck(checkId, { amount: newAmount })
      onUpdate()
      toast({
        title: "Updated",
        description: "Check amount updated successfully",
      })
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to update check amount",
      })
      setLocalValue(initial?.toString() ?? "")
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div class="flex items-center gap-2">
        <span class="text-3xl font-semibold text-gray-400">...</span>
        <LoadingSpinner />
      </div>
    )
  }

  if (editing) {
    return (
      <div class="flex items-center">
        <span class="text-3xl font-semibold text-gray-400">$</span>
        <input
          type="text"
          class="text-3xl font-semibold border-0 border-b-2 border-blue-500 p-0 outline-none focus:ring-0 bg-transparent w-40"
          value={localValue}
          autoFocus
          onChange={e => setLocalValue(e.currentTarget.value)}
          onKeyDown={e => {
            if (e.key === "Enter") {
              e.currentTarget.blur()
            } else if (e.key === "Escape") {
              setLocalValue(initial?.toString() ?? "")
              setEditing(false)
            }
          }}
          onBlur={handleSave}
        />
      </div>
    )
  }

  return (
    <div class="flex items-center gap-1">
      <span
        class="text-3xl font-semibold cursor-pointer hover:bg-gray-100 rounded px-1 -mx-1"
        onClick={() => setEditing(true)}
      >
        {initial != null ? exactCurrencyFormatter(initial) : "-"}
      </span>
      <TooltipProvider>
        <Tooltip open={tooltipOpen} onOpenChange={setTooltipOpen}>
          <TooltipTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              className="h-5 w-5 p-0 text-gray-400 hover:text-gray-600"
              onMouseEnter={() => setTooltipOpen(true)}
              onMouseLeave={() => setTooltipOpen(false)}
              onFocus={() => setTooltipOpen(true)}
              onBlur={() => setTooltipOpen(false)}
            >
              <Info className="h-3.5 w-3.5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            Use numbers or expressions like 100+20-5. We evaluate on blur/Tab and save the result.
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  )
}

interface EditableDateFieldProps {
  value: string | undefined
  checkId: string | undefined
  onUpdate: () => void
}

function EditableDateField({ value: initial, checkId, onUpdate }: EditableDateFieldProps) {
  const [loading, setLoading] = useState(false)
  const [editing, setEditing] = useState(false)
  const [localValue, setLocalValue] = useState(initial ?? "")
  const lastPropValue = useRef(initial)

  if (initial !== lastPropValue.current) {
    lastPropValue.current = initial
    setLocalValue(initial ?? "")
  }

  async function handleSave() {
    setEditing(false)
    if (localValue === initial) {
      return
    }
    setLoading(true)
    try {
      await updateCheck(checkId, { date: localValue })
      onUpdate()
      toast({
        title: "Updated",
        description: "Check date updated successfully",
      })
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to update check date",
      })
      setLocalValue(initial ?? "")
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div class="flex items-center gap-2">
        <span class="text-gray-400">...</span>
        <LoadingSpinner />
      </div>
    )
  }

  if (editing) {
    return (
      <input
        type="date"
        class="text-gray-500 border-0 border-b-2 border-blue-500 p-0 outline-none focus:ring-0 bg-transparent"
        value={localValue}
        autoFocus
        onChange={e => setLocalValue(e.currentTarget.value)}
        onKeyDown={e => {
          if (e.key === "Enter") {
            e.currentTarget.blur()
          } else if (e.key === "Escape") {
            setLocalValue(initial ?? "")
            setEditing(false)
          }
        }}
        onBlur={handleSave}
      />
    )
  }

  return (
    <span
      class="text-gray-500 cursor-pointer hover:bg-gray-100 rounded px-1 -mx-1"
      onClick={() => setEditing(true)}
    >
      {initial ? format(parseISO(initial), "PP") : "-"}
    </span>
  )
}

interface EditableNumberFieldProps {
  value: string
  checkId: string | undefined
  onUpdate: () => void
}

function EditableNumberField({ value: initial, checkId, onUpdate }: EditableNumberFieldProps) {
  const [loading, setLoading] = useState(false)
  const [editing, setEditing] = useState(false)
  const [localValue, setLocalValue] = useState(initial)
  // Number awaiting explicit user approval because it's already used by another check
  const [conflictNumber, setConflictNumber] = useState<string | null>(null)
  const lastPropValue = useRef(initial)

  if (initial !== lastPropValue.current) {
    lastPropValue.current = initial
    setLocalValue(initial)
  }

  async function save_number(number: string, allow_existing_number: boolean) {
    setLoading(true)
    try {
      await updateCheck(checkId, { number, allow_existing_number })
      onUpdate()
      toast({
        title: "Updated",
        description: "Check number updated successfully",
      })
    } catch (error) {
      if (!allow_existing_number && is_check_number_conflict(error)) {
        setConflictNumber(number)
        return
      }
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to update check number",
      })
      setLocalValue(initial)
    } finally {
      setLoading(false)
    }
  }

  async function handleSave() {
    setEditing(false)
    if (localValue === initial) {
      return
    }
    await save_number(localValue, false)
  }

  function handleConflictCancel() {
    setConflictNumber(null)
    setLocalValue(initial)
  }

  function handleConflictConfirm() {
    const number = conflictNumber!
    setConflictNumber(null)
    save_number(number, true)
  }

  const conflict_dialog = (
    <AlertDialog open={conflictNumber !== null} onOpenChange={(open: boolean) => !open && handleConflictCancel()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Check #{conflictNumber} already exists</AlertDialogTitle>
          <AlertDialogDescription>
            Another check (or its deductions) already uses this number. Renaming this check to #{conflictNumber} will
            make both checks share the same number, and their pages may show combined data. Only continue if you're
            sure this is the same check.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={handleConflictCancel}>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={handleConflictConfirm}>Use this number anyway</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )

  if (loading) {
    return (
      <div class="flex items-center gap-2">
        <span class="text-3xl text-primary text-gray-400">...</span>
        <LoadingSpinner />
      </div>
    )
  }

  if (editing) {
    return (
      <input
        type="text"
        class="text-3xl text-primary border-0 border-b-2 border-blue-500 p-0 outline-none focus:ring-0 bg-transparent"
        value={localValue}
        autoFocus
        onChange={e => setLocalValue(e.currentTarget.value)}
        onKeyDown={e => {
          if (e.key === "Enter") {
            e.currentTarget.blur()
          } else if (e.key === "Escape") {
            setLocalValue(initial)
            setEditing(false)
          }
        }}
        onBlur={handleSave}
      />
    )
  }

  return (
    <>
      <span
        class="text-3xl text-primary cursor-pointer hover:bg-gray-100 rounded px-1 -mx-1"
        onClick={() => setEditing(true)}
      >
        {initial || "-"}
      </span>
      {conflict_dialog}
    </>
  )
}

interface EditableCheckHeaderProps {
  check: CheckDetailData
  onUpdate: () => void
  children?: preact.ComponentChildren
}

function EditableCheckHeader({ check, onUpdate, children }: EditableCheckHeaderProps) {
  return (
    <div>
      <div class="flex flex-col mt-1 mb-2">
        <EditableAmountField value={check.amount} checkId={check.id} onUpdate={onUpdate} />
      </div>
      <EditableDateField value={check.date} checkId={check.id} onUpdate={onUpdate} />
      <div class="flex gap-8 items-center">
        <EditableNumberField value={check.number} checkId={check.id} onUpdate={onUpdate} />
        {children}
      </div>
      <h3 class="text-gray-500">
        {check.customer_name || check.source} Check
      </h3>
    </div>
  )
}

function shouldShowCheckPdfPane(
  checkS3Uri: string | undefined | null,
  backupS3Uri: string | undefined | null,
): boolean {
  return Boolean(checkS3Uri) || Boolean(backupS3Uri)
}

function useCheckPdfUpload(check: CheckDetailData) {
  const [uploading, setUploading] = useState(false)

  async function uploadFiles(files: File[]) {
    if (files.length === 0) return

    const file = files[0]
    if (!file.type.includes("pdf")) {
      toast({
        variant: "destructive",
        title: "Invalid file type",
        description: "Please upload a PDF file",
      })
      return
    }
    setUploading(true)
    try {
      const formData = new FormData()
      formData.append("files", file)
      formData.append(
        "upload_meta",
        JSON.stringify({
          file_kinds: [""],
          check_id: check.id,
        }),
      )

      const response = await fetch("/api/file", {
        method: "POST",
        credentials: "include",
        body: formData,
      })

      if (!response.ok) {
        throw new Error("Upload failed")
      }

      triggerRefreshFlag()
      toast({
        title: "Success",
        description: "Check file uploaded successfully",
      })
    } catch (error) {
      console.error("Failed to upload check file:", error)
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to upload check file",
      })
    } finally {
      setUploading(false)
    }
  }

  return { uploading, uploadFiles }
}

interface CheckPdfUploadButtonProps {
  check: CheckDetailData
}

function CheckPdfUploadButton({ check }: CheckPdfUploadButtonProps) {
  const { uploading, uploadFiles } = useCheckPdfUpload(check)
  const { open, getInputProps } = useDropzone({
    onDrop: uploadFiles,
    multiple: false,
    accept: {
      "application/pdf": [".pdf"],
    },
    noClick: true,
    noKeyboard: true,
  })

  return (
    <Button variant="outline" onClick={open} disabled={uploading}>
      {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
      Upload check PDF
      <input {...getInputProps()} />
    </Button>
  )
}

interface CheckDetailProps {
  params: { checkNumber: string }
}

export function CheckDetail({ params }: CheckDetailProps) {
  const checkNumber = decode_check_number(params.checkNumber)
  const refreshFlag = RefreshFlag.use(sf => sf!)
  const [selectedTransactionId, setSelectedTransactionId] = useState<string | null>(null)
  const [showExportWarning, setShowExportWarning] = useState(false)
  const [downloadingBackupZip, setDownloadingBackupZip] = useState(false)
  const [autoCodeOpen, setAutoCodeOpen] = useState(false)
  const [autoCodeTried, setAutoCodeTried] = useState(false)
  // Collapsing the check/backup pane hands the full width to the coding form, which is otherwise
  // squeezed into half the screen while splitting a transaction.
  const [pdfPaneCollapsed, setPdfPaneCollapsed] = useState(false)
  const orgSettings = useOrgSettings()

  useAsyncEffect(async () => {
    await Promise.all([loadOrgSettings(), loadOrgFieldConfigs()])
  }, [])

  const {
    loading,
    data: check,
    error,
  } = useFetch<CheckDetailData>(() => api_fetch_throws(check_api_path(checkNumber)), [checkNumber, refreshFlag])

  // Find the selected transaction from the check data
  const selectedTransaction = selectedTransactionId
    ? check?.transactions.find(t => t.id === selectedTransactionId) || null
    : null

  async function handleExportProductServices(checkNumber: string) {
    try {
      await download_export(
        `/api${check_api_path(checkNumber)}/export-product-services`,
        `check_${checkNumber}_product_services.xlsx`
      )
    } catch (error) {
      console.error("Failed to export product services:", error)
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to export product services",
      })
    }
  }

  async function handleDownloadBackupZip(checkNumber: string) {
    setDownloadingBackupZip(true)
    try {
      await download_export(
        `/api${check_api_path(checkNumber)}/backup-zip`,
        `Check_${checkNumber}_backup.zip`
      )
    } catch (error) {
      console.error("Failed to download backup ZIP:", error)
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to download backup ZIP",
      })
    } finally {
      setDownloadingBackupZip(false)
    }
  }

  // Only unmount for the initial load or a navigation to a different check. Background
  // refetches (RefreshFlag flips, e.g. a status change in the inline transaction detail)
  // keep the content mounted so unsaved splits survive.
  if (should_show_detail_spinner(loading, check?.number, checkNumber)) {
    return <CenterSpinner />
  }

  if (!check || (error && check.number !== checkNumber)) {
    return (
      <div class="flex justify-center items-center min-h-64 w-full">
        <div class="text-center">
          <div class="text-lg font-medium text-red-600">Error loading check</div>
          <p class="text-gray-600 mt-2">Could not find check #{checkNumber}</p>
          <Link to="/accounting/checks">
            <Button class="mt-4">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Checks
            </Button>
          </Link>
        </div>
      </div>
    )
  }

  const show_pdf_pane = shouldShowCheckPdfPane(check.s3_uri, selectedTransaction?.backup_s3_uri)

  return (
    <div class="w-full h-screen flex flex-col">
      <DetailNavbar />
      <div class="flex items-end justify-between mt-1 mb-3 mx-4 gap-4 shrink-0">
        <EditableCheckHeader check={check} onUpdate={() => triggerRefreshFlag()}>
          <AccountingStatus status={check.status} />
        </EditableCheckHeader>
        <div class="flex items-center gap-2 shrink-0">
          {!check.s3_uri && !show_pdf_pane && <CheckPdfUploadButton check={check} />}
          {check.source === "kehe" && (
            <Button
              variant="outline"
              disabled={downloadingBackupZip}
              onClick={() => handleDownloadBackupZip(check.number)}
              title="Download all backup files for this check as a ZIP, named by invoice number in check order"
            >
              {downloadingBackupZip ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              Backup ZIP
            </Button>
          )}
          {orgSettings.has_product_services && (
            <Button
              variant="outline"
              onClick={() => {
                if (check.status !== "coded") {
                  setShowExportWarning(true)
                } else {
                  handleExportProductServices(check.number)
                }
              }}
            >
              <Download className="w-4 h-4" />
              Export Items Report
            </Button>
          )}
          <Button
            variant="outline"
            onClick={() => {
              setAutoCodeTried(true)
              setAutoCodeOpen(true)
            }}
            title="Preview which deductions your simple coding rules would code"
          >
            <Wand2 className="w-4 h-4" />
            Preview auto code
          </Button>
          <CashApplicationControl
            check={check}
            onNavigateToUnassigned={(id) => setSelectedTransactionId(id)}
            onAppliedChange={() => triggerRefreshFlag()}
          />
        </div>
      </div>
      <CodingStrip
        transactions={check.transactions}
        has_accounting_system={Boolean(orgSettings.accounting_integration)}
        auto_code_tried={autoCodeTried}
        on_auto_code={() => {
          setAutoCodeTried(true)
          setAutoCodeOpen(true)
        }}
      />
      <div class="flex w-full px-4 gap-2 flex-1 min-h-0">
        <LeftPane
          check={check}
          selectedTransactionId={selectedTransactionId}
          onSelectTransaction={setSelectedTransactionId}
          className={show_pdf_pane && !pdfPaneCollapsed ? "flex-1 min-w-0" : "w-full flex-1 min-w-0"}
        />
        {show_pdf_pane &&
          (pdfPaneCollapsed ? (
            <CollapsedPaneRail
              label={selectedTransaction?.backup_s3_uri ? "Backup" : `Check ${check.number}`}
              onExpand={() => setPdfPaneCollapsed(false)}
            />
          ) : (
            <RightPane
              check={check}
              selectedTransaction={selectedTransaction}
              onCollapse={() => setPdfPaneCollapsed(true)}
            />
          ))}
      </div>

      <AutoCodePreviewDialog
        open={autoCodeOpen}
        onClose={() => setAutoCodeOpen(false)}
        check_number={check.number}
        check_deduction_ids={check.transactions.map(t => t.id)}
      />

      <AlertDialog open={showExportWarning} onOpenChange={setShowExportWarning}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Check not fully coded</AlertDialogTitle>
            <AlertDialogDescription>
              This check isn't fully coded. Are you sure you want to export?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Finish coding</AlertDialogCancel>
            <AlertDialogAction onClick={() => handleExportProductServices(check.number)}>
              Continue
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

interface LeftPaneProps {
  check: CheckDetailData
  selectedTransactionId: string | null
  onSelectTransaction: (id: string | null) => void
  className?: string
}

function LeftPane({ check, selectedTransactionId, onSelectTransaction, className }: LeftPaneProps) {
  const [activeTab, setActiveTab] = useState("rows")

  function handleDataChange() {
    triggerRefreshFlag()
  }

  function handleSelectTransaction(id: string | null) {
    onSelectTransaction(id)
    if (id) {
      setActiveTab("transaction")
    }
  }

  // Find the selected transaction and its index
  const selectedIndex = selectedTransactionId
    ? check.transactions.findIndex(t => t.id === selectedTransactionId)
    : -1
  const selectedTransaction = selectedIndex >= 0 ? check.transactions[selectedIndex] : null

  // Count coded transactions
  const codedCount = check.transactions.filter(
    t => t.kind === "deduction" && t.accounting_status === "coded",
  ).length
  const totalCount = check.transactions.length

  // Navigation handlers
  function handlePrevTransaction() {
    if (selectedIndex > 0) {
      handleSelectTransaction(check.transactions[selectedIndex - 1].id)
    }
  }

  function handleNextTransaction() {
    if (selectedIndex < check.transactions.length - 1) {
      handleSelectTransaction(check.transactions[selectedIndex + 1].id)
    }
  }

  return (
    <Tabs
      value={activeTab}
      onValueChange={setActiveTab}
      className={`grid grid-rows-[auto_1fr] ${className ?? "flex-1 min-w-0"}`}>
      <div class="mx-2 mt-2 flex items-center justify-between border-b border-gray-200">
        <TabsList className="h-auto bg-transparent p-0 rounded-none">
          <TabsTrigger
            value="rows"
            className="rounded-none border-b-2 border-transparent data-[state=active]:border-blue-600 data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 py-2 text-gray-600 data-[state=active]:text-blue-600">
            Check Rows
          </TabsTrigger>
          {selectedTransaction && (
            <TabsTrigger
              value="transaction"
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-blue-600 data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 py-2 text-gray-600 data-[state=active]:text-blue-600">
              {selectedTransaction.invoice_number || "Transaction"}
            </TabsTrigger>
          )}
        </TabsList>

        <div class="text-sm text-gray-500">
          {codedCount} coded
        </div>

        {selectedTransaction && (
          <div class="flex items-center gap-1 text-sm text-gray-600">
            <button
              onClick={handlePrevTransaction}
              disabled={selectedIndex <= 0}
              class="p-1 hover:bg-gray-100 rounded disabled:opacity-30 disabled:cursor-not-allowed">
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span>{selectedIndex + 1} of {totalCount}</span>
            <button
              onClick={handleNextTransaction}
              disabled={selectedIndex >= totalCount - 1}
              class="p-1 hover:bg-gray-100 rounded disabled:opacity-30 disabled:cursor-not-allowed">
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>
      <TabsContent value="rows" className="mt-0 min-h-0 overflow-auto">
        <TransactionTable
          check={check}
          onDataChange={handleDataChange}
          selectedTransactionId={selectedTransactionId}
          onSelectTransaction={handleSelectTransaction}
        />
      </TabsContent>
      {selectedTransaction && (
        <TabsContent value="transaction" className="mt-0 min-h-0 overflow-auto">
          <InlineTransactionDetail transaction={selectedTransaction} />
        </TabsContent>
      )}
    </Tabs>
  )
}

/**
 * Stand-in for the collapsed check/backup pane: a thin rail that keeps the pane one click away
 * without giving up any of the coding form's width.
 */
function CollapsedPaneRail({ label, onExpand }: { label: string; onExpand: () => void }) {
  return (
    <div class="shrink-0 border-l border-gray-200 bg-white flex flex-col items-center gap-2 py-2 px-1">
      <button
        onClick={onExpand}
        title={`Show ${label}`}
        aria-label={`Show ${label}`}
        class="p-1 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded">
        <PanelRightOpen className="h-4 w-4" />
      </button>
      <span class="text-xs text-gray-500 [writing-mode:vertical-rl] whitespace-nowrap">{label}</span>
    </div>
  )
}

interface RightPaneProps {
  check: CheckDetailData
  selectedTransaction: TransactionJson | null
  onCollapse: () => void
}

function RightPane({ check, selectedTransaction, onCollapse }: RightPaneProps) {
  const { uploading, uploadFiles } = useCheckPdfUpload(check)
  const [activeTab, setActiveTab] = useState("check")
  const refreshFlag = RefreshFlag.use()

  const { data: backupResponse, loading: backupLoading } = useFetch(
    () =>
      selectedTransaction
        ? api_fetch_throws<BackupViewResponse>(
            `/deduction/${selectedTransaction.id}/backup?include_raw=true`
          )
        : Promise.resolve({ rows: [], dynamic_table: null }),
    [selectedTransaction?.id, refreshFlag],
    { rows: [], dynamic_table: null }
  )

  // Switch to backup tab when a transaction with backup is selected
  const hasBackup = selectedTransaction?.backup_s3_uri
  const hasCheckFile = Boolean(check.s3_uri)

  // Get bounding box for selected transaction (if available)
  const highlightBox: BoundingBox | null = useMemo(() => {
    if (!selectedTransaction || !check.line_bounding_boxes) {
      return null
    }
    const bb = check.line_bounding_boxes[selectedTransaction.invoice_number]
    if (!bb) {
      return null
    }
    return bb
  }, [selectedTransaction?.invoice_number, check.line_bounding_boxes])

  // Convert page dimensions to the format expected by PdfViewer
  const pageDimensions = useMemo(() => {
    if (!check.page_dimensions) {
      return undefined
    }
    return check.page_dimensions
  }, [check.page_dimensions])

  // Check if the check PDF is a PDF (ends with .pdf)
  const isPdf = check.s3_uri?.toLowerCase().endsWith(".pdf")

  // Auto-switch to backup tab when a new transaction with backup is selected
  // Switch back to check tab when transaction has no backup
  useAsyncEffect(async () => {
    if (hasBackup && selectedTransaction) {
      setActiveTab("backup")
    } else if (hasCheckFile) {
      setActiveTab("check")
    }
  }, [selectedTransaction?.id, hasBackup, hasCheckFile])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: uploadFiles,
    onDropRejected: () => toast(NOT_A_CHECK_PDF_TOAST),
    multiple: false,
    accept: {
      "application/pdf": [".pdf"],
    },
  })

  return (
    <Tabs value={activeTab} onValueChange={setActiveTab} className="w-1/2 flex flex-col bg-white">
      <div class="mx-2 mt-2 flex items-center justify-between border-b border-gray-200 shrink-0">
        <TabsList className="h-auto bg-transparent p-0 rounded-none">
          {hasCheckFile && (
            <TabsTrigger
              value="check"
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-blue-600 data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 py-2 text-gray-600 data-[state=active]:text-blue-600">
              Check {check.number}
            </TabsTrigger>
          )}
          {hasBackup && selectedTransaction && (
            <TabsTrigger
              value="backup"
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-blue-600 data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 py-2 text-gray-600 data-[state=active]:text-blue-600">
              {selectedTransaction.invoice_number || "Transaction"} Backup
            </TabsTrigger>
          )}
        </TabsList>
        <button
          onClick={onCollapse}
          title="Hide this pane"
          aria-label="Hide this pane"
          class="p-1 mr-1 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded">
          <PanelRightClose className="h-4 w-4" />
        </button>
      </div>
      <div class="flex-1 min-h-0 relative">
        {hasCheckFile && (
        <TabsContent value="check" forceMount className="mt-0 absolute inset-0 data-[state=inactive]:invisible">
        {check.s3_uri ? (
          isPdf ? (
            <PdfViewer
              s3Uri={check.s3_uri}
              fileName={`Check ${check.number}`}
              highlightBox={highlightBox}
              pageDimensions={pageDimensions}
            />
          ) : (
            <FileViewer s3Uri={check.s3_uri} />
          )
        ) : (
          <div class="flex items-center justify-center h-full p-4">
            <div
              {...(getRootProps() as HTMLAttributes<HTMLDivElement>)}
              class={`w-full max-w-md border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer ${
                isDragActive ? "border-blue-500 bg-blue-50" : "border-gray-300 hover:border-gray-400"
              }`}>
              <input {...getInputProps()} />
              {uploading ? (
                <>
                  <Loader2 className="mx-auto h-12 w-12 text-gray-400 mb-4 animate-spin" />
                  <p class="text-lg font-medium text-gray-900 mb-2">Uploading...</p>
                </>
              ) : (
                <>
                  <Upload className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                  <p class="text-lg font-medium text-gray-900 mb-2">
                    {isDragActive ? "Drop the PDF here" : "No check image uploaded"}
                  </p>
                  <p class="text-sm text-gray-500">Drag and drop a PDF file to upload, or click to select</p>
                </>
              )}
            </div>
          </div>
        )}
        </TabsContent>
        )}
        {hasBackup && (
          <TabsContent value="backup" forceMount className="mt-0 absolute inset-0 data-[state=inactive]:invisible">
            {backupLoading ? (
              <div class="flex h-full items-center justify-center">
                <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
              </div>
            ) : (
              <AccountingFilePane
                transaction={selectedTransaction!}
                currentFile={{
                  s3Uri: selectedTransaction!.backup_s3_uri!,
                  fileName: selectedTransaction!.invoice_number
                    ? `${selectedTransaction!.invoice_number} Backup`
                    : "Backup",
                }}
                allFiles={[]}
                backupResponse={backupResponse}
                backupLoading={backupLoading}
                onFileSelect={() => {}}
              />
            )}
          </TabsContent>
        )}
      </div>
    </Tabs>
  )
}

const columnHelper = createColumnHelper<TransactionJson>()

const TRANSACTION_KIND_OPTIONS: { label: string; value: DeductionKind }[] = [
  { label: "Deduction", value: "deduction" },
  { label: "Repayment", value: "repayment" },
  { label: "Invoice", value: "invoice" },
  { label: "Prepayment", value: "prepayment" },
]

function transaction_kind_class(kind: DeductionKind): string {
  if (kind === "repayment") {
    return "bg-indigo-100 text-indigo-700"
  }
  if (kind === "deduction") {
    return "bg-orange-100 text-orange-700"
  }
  if (kind === "invoice") {
    return "bg-cyan-100 text-cyan-700"
  }
  return "bg-pink-100 text-pink-700"
}

interface EditableKindCellProps {
  transaction_id: string
  kind: DeductionKind
}

function EditableKindCell({ transaction_id, kind }: EditableKindCellProps) {
  const [local_kind, set_local_kind] = useState(kind)
  const [saving, set_saving] = useState(false)
  // Track only external prop changes; successful saves keep the cell local to avoid rerendering the full table.
  const last_prop_kind = useRef(kind)

  if (kind !== last_prop_kind.current) {
    last_prop_kind.current = kind
    set_local_kind(kind)
  }

  async function handle_kind_change(value: string) {
    const next_kind = value as DeductionKind
    if (next_kind === local_kind) {
      return
    }

    const previous_kind = local_kind
    set_local_kind(next_kind)
    set_saving(true)
    try {
      const response = await api_fetch(`/deduction/${transaction_id}`, {
        method: "PATCH",
        body: { kind: next_kind },
      })
      if (!response.ok) {
        throw new Error(response.error.message)
      }
      toast({
        title: "Transaction type updated",
        description: `This check row is now a ${next_kind}.`,
      })
    } catch (error) {
      set_local_kind(previous_kind)
      toast({
        variant: "destructive",
        title: "Failed to update transaction type",
        description: error instanceof Error ? error.message : "Please try again.",
      })
    } finally {
      set_saving(false)
    }
  }

  return (
    <div
      onClick={(event: MouseEvent) => event.stopPropagation()}
      onPointerDown={(event: PointerEvent) => event.stopPropagation()}>
      <Select disabled={saving} onValueChange={handle_kind_change} value={local_kind}>
        <SelectTrigger
          aria-label="Transaction type"
          className={`h-7 min-w-28 gap-1 border-0 px-2 py-0 text-xs capitalize shadow-none ${transaction_kind_class(local_kind)}`}>
          <SelectValue />
          <ChevronDown className="h-3 w-3 shrink-0 opacity-70" />
        </SelectTrigger>
        <SelectContent>
          {TRANSACTION_KIND_OPTIONS.map((option: { label: string; value: DeductionKind }) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}

interface TransactionTableProps {
  check: CheckDetailData
  onDataChange: () => void
  selectedTransactionId: string | null
  onSelectTransaction: (id: string | null) => void
}

function TransactionTable({ check, onDataChange, selectedTransactionId, onSelectTransaction }: TransactionTableProps) {
  const checkNumber = check.number
  const tableState = CheckDetailState.use(checkNumber)
  const orgConfig = OrgConfigState.use()
  const user = UserState.use()
  const ledgerAccounts = LedgerAccounts.use()

  useAsyncEffect(async () => {
    if (ledgerAccounts.length === 0) {
      await loadAccountCodes()
    }
  }, [])

  async function handleQuickCode(transactionId: string, amount: number, match: AccountSelectItem) {
    const res = await api_fetch("/match-deductions", {
      method: "POST",
      body: [
        {
          deduction_id: transactionId,
          reason_code_id: match.id,
        },
      ],
    })
    if (!res.ok) {
      toast({
        variant: "destructive",
        title: "Error",
        description: res.error.message || "Failed to code transaction",
      })
      return
    }
    toast({
      title: "Success",
      description: "Transaction coded successfully",
    })
    onDataChange()
  }

  const columns = useMemo(
    () => {
      const orgColumns = user
        ? createAccountingOrgColumns(user.org_id!, orgConfig.orgFieldConfigs, "check_detail_table")
        : []

      return [
        selectColumn as any,
        columnHelper.display({
          id: "line_number",
          header: ({ column }) => <ColumnHeader column={column} title="#" />,
          cell: ({ row }) => <span>{row.index + 1}</span>,
        }),
        columnHelper.accessor("invoice_number", {
          header: ({ column }) => <ColumnHeader column={column} title="Transaction #" />,
          cell: info => {
            const value = info.getValue() as string
            return <CopyableCell value={value} />
          },
        }),
        columnHelper.accessor("invoice_date", {
          header: ({ column }) => <ColumnHeader column={column} title="Date" />,
          cell: info => <span>{info.getValue() ? format(parseISO(info.getValue()), "PP") : "-"}</span>,
          sortingFn: "datetime",
        }),
        columnHelper.accessor("invoice_amount", {
          header: ({ column }) => <ColumnHeader className="justify-end" column={column} title="Amount" />,
          cell: info => (
            <div class="text-right font-medium">
              {exactCurrencyFormatter(
                display_coding_amount(info.row.original.kind, info.getValue() as number, info.row.original.discount_amount)
              )}
            </div>
          ),
        }),
        // Accessor is the coding display text ("" when uncoded) so the column can sort
        // (asc groups uncoded rows first) and filter by coded/uncoded.
        columnHelper.accessor(row => coding_display_text(row.splits).text, {
          id: "coding",
          header: ({ column }) => (
            <ColumnHeader
              column={column}
              title="Coding"
              choices={["coded", "uncoded"]}
              onSelectChoice={choice => column.setFilterValue(choice === "all" ? undefined : choice)}
            />
          ),
          filterFn: (row, columnId, filterValue) =>
            (row.getValue<string>(columnId) ? "coded" : "uncoded") === filterValue,
          cell: ({ row }) => {
            const transaction = row.original
            // An invoice paid with no discount, or a UNFI PP row whose cash the server refuses to
            // code, gets no "+ Code" pill: one there can only fail.
            const nothing_to_code = (transaction.splits || []).length === 0 ? coding_pill_notice(transaction) : null
            if (nothing_to_code) {
              return (
                <span class="text-xs text-gray-400" title={nothing_to_code}>
                  Nothing to code
                </span>
              )
            }
            return (
              <div class="flex items-center gap-2" onClick={(e: MouseEvent) => e.stopPropagation()}>
                <CodingCell
                  splits={transaction.splits || []}
                  ledgerAccounts={ledgerAccounts}
                  onSelect={match => handleQuickCode(transaction.id, transaction.invoice_amount, match)}
                />
                {/* Live status while a bulk CodingWiz run works on this row, then a wand to open the result. */}
                <BulkCodingWizRowBadge deduction_id={transaction.id} />
              </div>
            )
          },
        }),
        categoryColumn as any,
        categoryDescriptionColumn as any,
        dcColumn as any,
        backupColumn as any,
        statusColumn as any,
        columnHelper.accessor("kind", {
          header: ({ column }) => <ColumnHeader column={column} title="Kind" />,
          cell: info => <EditableKindCell transaction_id={info.row.original.id} kind={info.getValue()} />,
          filterFn: "equals",
        }),
        ...orgColumns,
        columnHelper.accessor(row => backup_parse_sort_rank(row), {
          id: "backup_parse",
          header: ({ column }) => <ColumnHeader column={column} title="Backup parsed" />,
          enableHiding: true,
          sortingFn: (a, b) => backup_parse_sort_rank(a.original) - backup_parse_sort_rank(b.original),
          cell: ({ row }) => {
            const { text, title } = backup_parse_cell_label(row.original)
            const parsed = row.original.backup_parsed === true
            if (parsed) {
              return (
                <span class="inline-flex" title={title}>
                  <Badge variant="outline" className="text-emerald-800 border-emerald-300 bg-emerald-50">
                    {text}
                  </Badge>
                </span>
              )
            }
            if ((row.original.backup_s3_uri ?? "").toLowerCase().endsWith(".pdf")) {
              return (
                <span class="inline-flex" title={title}>
                  <Badge variant="outline" className="text-amber-900 border-amber-300 bg-amber-50">
                    {text}
                  </Badge>
                </span>
              )
            }
            return (
              <span class="text-muted-foreground tabular-nums" title={title}>
                {text}
              </span>
            )
          },
        }),
      ]
    },
    [ledgerAccounts, orgConfig.orgFieldConfigs, user?.org_id]
  )

  const table = useReactTable({
    data: check.transactions,
    columns,
    state: {
      sorting: tableState.sorting,
      columnFilters: tableState.columnFilters,
      rowSelection: tableState.rowSelection,
      globalFilter: tableState.globalFilter,
    },
    onSortingChange: updater => {
      const newSorting = typeof updater === "function" ? updater(tableState.sorting) : updater
      CheckDetailState.setSorting(checkNumber, newSorting)
    },
    onColumnFiltersChange: updater => {
      const newFilters = typeof updater === "function" ? updater(tableState.columnFilters) : updater
      CheckDetailState.setColumnFilters(checkNumber, newFilters)
    },
    onRowSelectionChange: updater => {
      const newSelection = typeof updater === "function" ? updater(tableState.rowSelection) : updater
      CheckDetailState.setRowSelection(checkNumber, newSelection)
    },
    onGlobalFilterChange: updater => {
      const newFilter = typeof updater === "function" ? updater(tableState.globalFilter) : updater
      CheckDetailState.setGlobalFilter(checkNumber, newFilter)
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    globalFilterFn: fuzzyFilter,
    getColumnCanGlobalFilter: c => c.columnDef.enableGlobalFilter ?? true,
    enableRowSelection: true,
    initialState: {
      columnVisibility: {
        backup_parse: false,
        admin_fee: false,
      },
    },
    meta: {
      resetRowSelection: () => {
        CheckDetailState.setRowSelection(checkNumber, {})
      },
      clearAllFilters: () => {
        CheckDetailState.clearFilters(checkNumber)
      },
    },
  })

  function handleRowClick(row: Row<TransactionJson>, event: MouseEvent) {
    if (table.getIsSomeRowsSelected()) {
      row.toggleSelected()
      return
    }
    if (event.metaKey || event.ctrlKey) {
      window.open(`/accounting/transaction/${row.original.id}`, "_blank")
    } else {
      onSelectTransaction(row.original.id)
    }
  }

  // Guayaki-only export hook: optionally run the RTM auto-split scoped to this check's rows, then
  // refetch the check and return only the rows currently visible in the filtered table (transactionIds).
  async function getExportRows({
    autoSplit,
    transactionIds,
  }: {
    autoSplit: boolean
    transactionIds: string[]
  }): Promise<TransactionJson[]> {
    const idSet = new Set(transactionIds)
    function filterToExportScope(rows: TransactionJson[]): TransactionJson[] {
      return rows.filter((row: TransactionJson) => idSet.has(row.id))
    }

    if (autoSplit) {
      const res = await api_fetch<{ split_count: number }>("/auto-split-rtm", {
        method: "POST",
        body: { deduction_ids: transactionIds },
      })
      if (!res.ok) {
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to auto-split RTM before export",
        })
        // Fall through and export the current rows rather than blocking the export entirely.
        return filterToExportScope(check.transactions)
      }
      const split_count = res.value.data?.split_count ?? 0
      if (split_count === 0) {
        return filterToExportScope(check.transactions)
      }
      triggerRefreshFlag()
    }
    const fresh = await api_fetch_throws<CheckDetailData>(check_api_path(checkNumber))
    return filterToExportScope(fresh.transactions)
  }

  const rows = table.getRowModel().rows
  const isRowSelected = Object.keys(tableState.rowSelection).length > 0

  return (
    <>
      <TableControls
        table={table as any as Table<DeductionListJson>}
        orgFieldConfigs={orgConfig.orgFieldConfigs}
        enableCsvColumnPicker={true}
        csvColumnPickerScope="accounting-check-detail"
        getExportRows={isGuayakiUser(user?.org_id) ? (getExportRows as any) : undefined}
      />
      <div class="border border-gray-200 rounded-lg w-full overflow-hidden">
        <div class="overflow-auto w-full">
          <table class="w-full border-collapse" style={{ tableLayout: "auto" }}>
            <thead class="bg-slate-50">
              {table.getHeaderGroups().map((headerGroup, idx) => (
                <tr key={`${headerGroup.id}-${idx}`} className="border-b border-gray-200">
                  {headerGroup.headers.map((header, headerIdx) => (
                    <th
                      key={`${header.id}-${headerIdx}`}
                      className={`px-2 py-2 text-sm font-medium text-gray-900 whitespace-nowrap ${
                        header.column.id === "invoice_amount" || header.column.id === "discount_amount"
                          ? "text-right"
                          : "text-left"
                      }`}
                      style={{
                        position: "sticky",
                        top: 0,
                        backgroundColor: "#f8fafc",
                        zIndex: 10,
                        boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
                        maxWidth: "180px",
                        width: "auto",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                      title={header.column.id ? capitalCase(header.column.id) : ""}>
                      {header.isPlaceholder ? null : (
                        <div
                          className={
                            header.column.id === "invoice_amount" || header.column.id === "discount_amount"
                              ? "flex justify-end items-center"
                              : ""
                          }>
                          {flexRender(header.column.columnDef.header, header.getContext())}
                        </div>
                      )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {rows.length ? (
                rows.map((row, idx) => {
                  let isSelected = selectedTransactionId === row.original.id
                  return (
                    <tr
                      key={`${row.id}-${idx}`}
                      className={`hover:bg-slate-50 cursor-pointer ${isSelected ? "bg-blue-100" : ""}`}
                      onClick={(event: MouseEvent) => handleRowClick(row, event)}
                      data-state={row.getIsSelected() && "selected"}>
                      {row.getVisibleCells().map((cell, cellIdx) => {
                        // GL coding names are long; wrap them instead of clipping so the whole code shows.
                        const isCoding = cell.column.id === "coding"
                        return (
                          <td
                            className={`px-2 py-2 border-b border-gray-200 text-sm ${isCoding ? "" : "whitespace-nowrap"}`}
                            style={{
                              maxWidth: isCoding ? "320px" : "180px",
                              minWidth: isCoding ? "220px" : undefined,
                              width: "auto",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                            title={String(cell.getValue() || "")}
                            key={`${cell.id}-${cellIdx}`}>
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </td>
                        )
                      })}
                    </tr>
                  )
                })
              ) : (
                <tr>
                  <td colSpan={columns.length} className="h-24 text-center">
                    No results.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {isRowSelected && (
        <DeductionTableSelectionBar
          table={table as any as Table<DeductionListJson>}
          isAccounting={true}
          orgFieldConfigs={orgConfig.orgFieldConfigs}
        />
      )}
    </>
  )
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  describe("shouldShowCheckPdfPane", () => {
    it("returns false when neither check file nor backup exists", () => {
      expect(shouldShowCheckPdfPane(undefined, undefined)).toBe(false)
      expect(shouldShowCheckPdfPane(null, null)).toBe(false)
    })

    it("returns true when check file exists", () => {
      expect(shouldShowCheckPdfPane("s3://bucket/check.pdf", undefined)).toBe(true)
    })

    it("returns true when backup exists without check file", () => {
      expect(shouldShowCheckPdfPane(undefined, "s3://bucket/backup.pdf")).toBe(true)
    })
  })
}
